import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { resolveVideo } from '../../lib/video';

// Click-to-play only — never autoplays audio on page load. Handles
// YouTube, Vimeo, hosted MP4, and plain external links (opens in a new tab
// rather than being embedded), and renders a clear "unavailable" state
// instead of a broken player for anything it can't resolve.
export default function VideoPlayer({ video, aspectRatio = '16 / 9' }) {
  const [playing, setPlaying] = useState(false);
  const resolved = resolveVideo(video);

  if (!resolved) {
    return (
      <Box
        role="status"
        sx={{
          aspectRatio,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          color: 'text.secondary',
          p: 3
        }}
      >
        <ErrorOutlineRoundedIcon fontSize="small" />
        <Typography variant="body2">This video is currently unavailable.</Typography>
      </Box>
    );
  }

  if (resolved.kind === 'external') {
    return (
      <Box
        component="a"
        href={resolved.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Watch on ${resolved.hostname}: ${resolved.title || 'video'} (opens in a new tab)`}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: '#000',
          textDecoration: 'none',
          '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: '2px' }
        }}
      >
        {resolved.thumbnailUrl ? (
          <Box
            component="img"
            src={resolved.thumbnailUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.85 }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        )}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <IconButton
            component="span"
            size="large"
            sx={{ bgcolor: 'rgba(255,255,255,0.92)', color: '#111', '&:hover': { bgcolor: '#fff' }, width: 64, height: 64 }}
          >
            <OpenInNewRoundedIcon fontSize="medium" />
          </IconButton>
          <Typography
            sx={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              px: 1,
              py: 0.25,
              borderRadius: 1
            }}
          >
            Watch on {resolved.hostname}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!playing) {
    return (
      <Box
        component="button"
        type="button"
        onClick={() => setPlaying(true)}
        aria-label={`Play video: ${resolved.title || 'video'}`}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          aspectRatio,
          p: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: '#000',
          '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: '2px' }
        }}
      >
        {resolved.thumbnailUrl ? (
          <Box
            component="img"
            src={resolved.thumbnailUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.85 }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        )}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <IconButton
            component="span"
            size="large"
            sx={{
              bgcolor: 'rgba(255,255,255,0.92)',
              color: '#111',
              '&:hover': { bgcolor: '#fff' },
              width: 64,
              height: 64
            }}
          >
            <PlayArrowRoundedIcon fontSize="large" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  if (resolved.kind === 'iframe') {
    return (
      <Box sx={{ aspectRatio, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Box
          component="iframe"
          src={resolved.embedUrl}
          title={resolved.title || 'Video player'}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="video"
      controls
      autoPlay
      playsInline
      poster={resolved.thumbnailUrl || undefined}
      sx={{ width: '100%', aspectRatio, borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'block', bgcolor: '#000' }}
    >
      <source src={resolved.src} type="video/mp4" />
      {video.captionsUrl ? <track kind="captions" src={video.captionsUrl} srcLang="en" label="Captions" default /> : null}
    </Box>
  );
}
